// src/store/taskStore.ts
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { Task } from "@/types";
import { tasksApi } from "@/api/endpoints/tasks";
import toast from "react-hot-toast";
import { useAuthStore } from "./authStore";

interface TaskState {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    pages: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  stats: any[];
  selectedTasks: string[];
  filters: {
    status?: string;
    priority?: string;
    client?: string;
    teamLeader?: string;
    allottedTo?: string;
    dueDateFrom?: string;
    dueDateTo?: string;
    search?: string;
  };
  sortConfig: {
    sortBy: string;
    sortOrder: "asc" | "desc";
  };
  // Actions
  filterCounts: {
    [key: string]: {
      count: number;
      totalTime: number;
    };
  };
  fetchTasks: (params?: any) => Promise<void>;
  createTask: (task: any) => Promise<void>;
  updateTask: (id: string, updates: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  bulkUpdateTasks: (ids: string[], updates: Partial<Task>) => Promise<void>;

  // Selection actions
  toggleTaskSelection: (id: string) => void;
  toggleAllTasks: (selected: boolean) => void;
  clearSelection: () => void;

  // Filter actions
  setFilters: (filters: any) => void;
  clearFilters: () => void;

  // Sort actions
  setSortConfig: (config: {
    sortBy: string;
    sortOrder: "asc" | "desc";
  }) => void;

  // Export actions
  exportTasks: (format: "csv" | "excel") => Promise<void>;
  importTasks: (file: File) => Promise<void>;

  // Helper methods for new role system
  getMyTasks: () => Task[];
  getTasksWhereImTeamLeader: () => Task[];
  canEditTask: (taskId: string) => boolean;
  canDeleteTask: (taskId: string) => boolean;
  canUpdateTaskTime: (taskId: string) => boolean;

    getOverdueTasks: () => Promise<Task[]>;

}

export const useTaskStore = create<TaskState>()(
  devtools(
    persist(
      (set, get) => ({
        tasks: [],
        loading: true,
        error: null,
        pagination: {
          total: 0,
          pages: 0,
          page: 1,
          limit: 20,
          hasNext: false,
          hasPrev: false,
        },
        stats: [],
        selectedTasks: [],
        filters: {},
        sortConfig: {
          sortBy: "createdAt",
          sortOrder: "desc",
        },
        filterCounts: {},
        fetchTaskCounts: async () => {
          try {
            const response = await tasksApi.getTaskCounts();
            const counts = response.data.counts || {};
            const formatted = Object.entries(counts).reduce(
              (acc, [key, val]) => {
                acc[key] = { count: val, totalTime: 0 };
                return acc;
              },
              {} as Record<string, { count: number; totalTime: number }>
            );
            set({ filterCounts: formatted });
          } catch (err) {
            console.error("Failed to fetch task counts", err);
            set({ filterCounts: {} });
          }
        },
        fetchTasks: async (params) => {
          set({ loading: true, error: null });
          try {
            const { filters, sortConfig } = get();
             // Clean filters - remove empty arrays and falsy values
    const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        acc[key] = value;
      } else if (!Array.isArray(value) && value) {
        acc[key] = value;
      }
      // Empty arrays are not sent to API (meaning no filter = all items)
      return acc;
    }, {});
        console.log("🚀 Fetching tasks with filters:", cleanedFilters);

            const response = await tasksApi.getTasks({
              ...filters,
              ...sortConfig,
              ...params,
            });

            const data = response.data;
            set({
              tasks: data?.data?.tasks || [],
              pagination: data.pagination || {
                total: 0,
                pages: 0,
                page: 1,
                limit: 20,
                hasNext: false,
                hasPrev: false,
              },
              stats: data.stats || [],
              filterCounts: data.filterCounts || {}, // Add this line
              loading: false,
            });
          } catch (error: any) {
            set({
              error: error.response?.data?.message || error.message,
              loading: false,
              tasks: [],
            });
            toast.error("Failed to fetch tasks");
          }
        },
createTask: async (taskData) => {
  console.log("taskData: ", taskData);
  try {
    const response = await tasksApi.createTask(taskData);
    const newTask =
      response.data.task || response.data.data?.task || response.data;
    console.log('🔴 Store: createTask response:', newTask);

    set((state) => ({
      tasks: [newTask, ...state.tasks],
      pagination: state.pagination
        ? {
            ...state.pagination,
            total: state.pagination.total + 1,
          }
        : {
            total: 1,
            pages: 1,
            page: 1,
            limit: 20,
            hasNext: false,
            hasPrev: false,
          },
    }));
    toast.success("Task created successfully");
    
    // IMPORTANT: Return the created task!
    return newTask;
  } catch (error: any) {
    console.error('🔴 Store: createTask error:', error);

    if (error.response?.status === 422) {
      const errorData = error.response.data;
      if (errorData.error?.errors) {
        errorData.error.errors.forEach((err: any) => {
          toast.error(err.msg || err.message || "Validation error");
        });
      } else {
        toast.error(errorData.message || "Validation failed");
      }
    } else {
      toast.error(
        error.response?.data?.message || "Failed to create task"
      );
    }
    throw error;
  }
},

        updateTask: async (id, updates) => {
          try {
            const response = await tasksApi.updateTask(id, updates);
            const updatedTask = response.data.task || response.data.data?.task;

            set((state) => ({
              tasks: state.tasks.map((task) =>
                (task._id || task.id) === id ? updatedTask : task
              ),
            }));
            toast.success("Task updated successfully");
          } catch (error: any) {
            console.log("error: ", error);
            toast.error(
              error.response?.data?.message || "Failed to update task"
            );
            throw error;
          }
        },
        deleteTask: async (id) => {
          try {
            await tasksApi.deleteTask(id);
            set((state) => ({
              tasks: state.tasks.filter((task) => (task._id || task.id) !== id),
              selectedTasks: state.selectedTasks.filter(
                (taskId) => taskId !== id
              ),
              pagination: {
                ...state.pagination,
                total: Math.max(0, state.pagination.total - 1),
              },
            }));
            toast.success("Task deleted successfully");
          } catch (error: any) {
            console.log('error: ', error);
            toast.error(
              error.response?.data?.message || "Failed to delete task"
            );
          }
        },

        bulkUpdateTasks: async (ids, updates) => {
          try {
            const response = await tasksApi.bulkUpdateTasks(ids, updates);
            await get().fetchTasks();
            toast.success(
              `${response.data.updated} tasks updated successfully`
            );
          } catch (error: any) {
            toast.error(
              error.response?.data?.message || "Failed to update tasks"
            );
          }
        },
        getOverdueTasks: async () => {
          set({ loading: true, error: null });
          try {
            const response = await tasksApi.getOverdueTasks(); // call API
            const overdueTasks = response.data.tasks || [];

            // Optionally update the store or just return
            set((state) => ({
              tasks: overdueTasks, // if you want to replace tasks with overdue tasks
              loading: false,
            }));

            return overdueTasks; // return for manual UI usage
          } catch (error: any) {
            console.log('error: ', error);
            set({
              loading: false,
              error:
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch overdue tasks",
            });
            toast.error("Failed to fetch overdue tasks");
            return [];
          }
        },
        toggleTaskSelection: (id) => {
          set((state) => ({
            selectedTasks: state.selectedTasks.includes(id)
              ? state.selectedTasks.filter((taskId) => taskId !== id)
              : [...state.selectedTasks, id],
          }));
        },

        toggleAllTasks: (selected) => {
          set((state) => ({
            selectedTasks: selected
              ? state.tasks.map((t) => t._id || t.id || "")
              : [],
          }));
        },

        clearSelection: () => {
          set({ selectedTasks: [] });
        },

        setFilters: (filters) => {
            console.log('🏪 Store setFilters called with:', filters);
          set({ filters });
          get().fetchTasks();
        },

        clearFilters: () => {
          set({ filters: {} });
          get().fetchTasks();
        },

        setSortConfig: (config) => {
          set({ sortConfig: config });
          get().fetchTasks();
        },

        exportTasks: async (format) => {
          try {
            const response = await tasksApi.exportTasks(format);
            const blob = new Blob([response.data], {
              type:
                format === "csv"
                  ? "text/csv"
                  : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `tasks-${Date.now()}.${
              format === "csv" ? "csv" : "xlsx"
            }`;
            link.click();
            URL.revokeObjectURL(url);
            toast.success("Tasks exported successfully");
          } catch (error: any) {
            toast.error(
              error.response?.data?.message || "Failed to export tasks"
            );
          }
        },

        importTasks: async (file) => {
          try {
            const response = await tasksApi.importTasks(file);
            await get().fetchTasks();
            toast.success(
              `${response.data.imported} tasks imported successfully`
            );
          } catch (error: any) {
            toast.error(
              error.response?.data?.message || "Failed to import tasks"
            );
          }
        },

        // Helper methods for new role system
        getMyTasks: () => {
          const { tasks } = get();
          const user = useAuthStore.getState().user;
          if (!user) return [];

          const userId = user._id || user.id;
          return tasks.filter((task) => {
            const assignedUserId =
              typeof task.allottedTo === "object"
                ? task.allottedTo._id || task.allottedTo.id
                : task.allottedTo;
            const teamLeaderId =
              typeof task.teamLeader === "object"
                ? task.teamLeader._id || task.teamLeader.id
                : task.teamLeader;

            return assignedUserId === userId || teamLeaderId === userId;
          });
        },

        getTasksWhereImTeamLeader: () => {
          const { tasks } = get();
          const user = useAuthStore.getState().user;
          if (!user) return [];

          const userId = user._id || user.id;
          return tasks.filter((task) => {
            const teamLeaderId =
              typeof task.teamLeader === "object"
                ? task.teamLeader._id || task.teamLeader.id
                : task.teamLeader;

            return teamLeaderId === userId;
          });
        },

        canEditTask: (taskId) => {
          const { tasks } = get();
          const authStore = useAuthStore.getState();
          const task = tasks.find((t) => (t._id || t.id) === taskId);

          if (!task) return false;
          return authStore.canManageTask(task);
        },

        canDeleteTask: (taskId) => {
          const authStore = useAuthStore.getState();
          return authStore.canDeleteTask();
        },

        canUpdateTaskTime: (taskId) => {
          const { tasks } = get();
          const authStore = useAuthStore.getState();
          const task = tasks.find((t) => (t._id || t.id) === taskId);

          if (!task) return false;
          return authStore.canEditTaskStatus(task);
        },
      }),
      {
        name: "task-store",
        partialize: (state) => ({
          filters: state.filters,
          sortConfig: state.sortConfig,
        }),
      }
    )
  )
);
